import { useRouter } from 'next/navigation';
import { createContext, RefObject, useCallback, useContext, useEffect } from 'react';

/** Asks before a navigation goes ahead; `leave` carries it out. */
export type AskToLeave = (leave: () => void) => void;

// TypeScript 5.9's DOM types have no Navigation API; these are the members used here.
type Navigation = EventTarget & { traverseTo: (key: string) => unknown };
type NavigateEvent = Event & { navigationType: string; destination: { key: string } };

/** The ask of the screen holding unsaved work, if any. */
export const LeaveGuardContext = createContext<RefObject<AskToLeave | null>>({ current: null });

/** While `active`, guarded navigations, browser Back/Forward and a closing tab ask through `ask` first. */
export const useLeaveGuard = (active: boolean, ask: AskToLeave) => {
  const guard = useContext(LeaveGuardContext);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    guard.current = ask;
    const { navigation } = globalThis as { navigation?: Navigation };
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    // A browser that cannot cancel this traversal leaves without asking.
    const traverse = (event: Event) => {
      const { navigationType, destination } = event as NavigateEvent;
      if (guard.current !== ask || navigationType !== 'traverse' || !event.cancelable) {
        return;
      }
      event.preventDefault();
      ask(() => {
        guard.current = null;
        void navigation?.traverseTo(destination.key);
      });
    };

    window.addEventListener('beforeunload', warn);
    navigation?.addEventListener('navigate', traverse);
    return () => {
      if (guard.current === ask) {
        guard.current = null;
      }
      window.removeEventListener('beforeunload', warn);
      navigation?.removeEventListener('navigate', traverse);
    };
  }, [active, ask, guard]);
};

/** Goes to `href`, asking first while a screen guards its work. A link passes the event its `onNavigate` gets. */
export const useGuardedNavigation = () => {
  const guard = useContext(LeaveGuardContext);
  const router = useRouter();

  return useCallback(
    (href: string, link?: { preventDefault: () => void }) => {
      const ask = guard.current;
      if (ask === null) {
        if (link === undefined) {
          router.push(href);
        }
        return;
      }
      link?.preventDefault();
      ask(() => {
        guard.current = null;
        router.push(href);
      });
    },
    [guard, router]
  );
};
